// services/spotify.js
import AuthService from './auth';

const DEBUG_SPOTIFY_LOGS = false;
const debugLog = (...args) => {
    if (DEBUG_SPOTIFY_LOGS) {
        console.log(...args);
    }
};
const debugWarn = (...args) => {
    if (DEBUG_SPOTIFY_LOGS) {
        console.warn(...args);
    }
};

class SpotifyService {
    constructor() {
        this.baseUrl = 'https://api.spotify.com/v1';
        this.cache = new Map();
        this.cacheExpiration = 5 * 60 * 1000; // 5 minutes
        this.minDelayBetweenRequests = 200; // 200ms tra richieste (aumentato per evitare 429)
        this.maxConcurrentRequests = 5; // Massimo 5 richieste parallele
        this.activeRequests = 0; // Contatore richieste attive
        this.requestQueue = []; // Coda per le richieste
        this.rateLimitResetTime = null;
        this.pendingRequests = new Map(); // Evita chiamate duplicate in parallelo
    }

    /**
     * Normalizza un nome artista per il matching esatto
     * Rimuove virgolette (esterne e interne), normalizza spazi, gestisce caratteri speciali
     */
    normalizeArtistName(name) {
        if (!name) return '';
        return name
            .trim()
            // Rimuovi TUTTE le virgolette (doppie e singole) - sia esterne che interne
            .replace(/["']/g, '')
            // Normalizza spazi multipli
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
    }

    // Delay utility
    async delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async request(endpoint, options = {}) {
        const cacheKey = this.getCacheKey(endpoint, options);
        const cachedData = this.getFromCache(cacheKey);
        
        if (cachedData) {
            return cachedData;
        }

        // Evita chiamate duplicate in parallelo per la stessa richiesta
        if (this.pendingRequests.has(cacheKey)) {
            debugLog(`[Spotify] Request already in progress for ${endpoint}, waiting...`);
            return this.pendingRequests.get(cacheKey);
        }

        // Sistema di queue per limitare richieste parallele
        return new Promise((resolve, reject) => {
            const processRequest = async () => {
                // Aspetta se abbiamo troppe richieste attive
                while (this.activeRequests >= this.maxConcurrentRequests) {
                    await this.delay(50); // Controlla ogni 50ms
                }

                this.activeRequests++;
                
                try {
                    const result = await this.executeRequest(endpoint, options, cacheKey);
                    resolve(result);
                } catch (error) {
                    reject(error);
                } finally {
                    this.activeRequests--;
                    // Processa la prossima richiesta in coda
                    if (this.requestQueue.length > 0) {
                        const nextRequest = this.requestQueue.shift();
                        nextRequest();
                    }
                }
            };

            // Se possiamo processare subito, fallo, altrimenti metti in coda
            if (this.activeRequests < this.maxConcurrentRequests) {
                processRequest();
            } else {
                this.requestQueue.push(processRequest);
            }
        });
    }

    async executeRequest(endpoint, options, cacheKey) {
        // Aggiungi delay se stiamo aspettando il rate limit reset
        if (this.rateLimitResetTime && Date.now() < this.rateLimitResetTime) {
            const waitTime = this.rateLimitResetTime - Date.now();
            debugLog(`[Spotify] Rate limit active, waiting ${Math.ceil(waitTime / 1000)}s...`);
            await this.delay(waitTime);
            this.rateLimitResetTime = null;
        }
    
        // Crea promise per deduplicazione
        // IMPORTANTE: crea la promise PRIMA di usarla dentro la IIFE
        let requestPromise;
        requestPromise = (async () => {
            const maxRetries = 3;
            let lastError = null;
            
            for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                    // Delay tra tentativi
                    if (attempt > 0) {
                        const backoffDelay = Math.min(1000 * Math.pow(2, attempt), 5000);
                        debugLog(`[Spotify] Retry ${attempt + 1}/${maxRetries} after ${backoffDelay}ms...`);
                        await this.delay(backoffDelay);
                    }

                    // Delay minimo tra richieste per evitare burst
                    await this.delay(this.minDelayBetweenRequests);

                    // Prova prima il token utente, poi il client credentials
                    let token = AuthService.getToken();
                    let isUserToken = true;
                    
                    // Se non c'è token utente o è scaduto, usa client credentials
                    // Se abbiamo ricevuto 429 nell'attempt precedente, FORZA nuova credenziale
                    if (!token || (attempt > 0 && lastError && lastError.message && lastError.message.includes('429'))) {
                        try {
                            // Se abbiamo ricevuto 429, forza nuova credenziale
                            const forceNew = (attempt > 0 && lastError && lastError.message && lastError.message.includes('429'));
                            token = await AuthService.getClientCredentialsToken(forceNew);
                            isUserToken = false;
                            
                            if (forceNew) {
                                debugLog('🔄 [Spotify] Rotating to next credential after 429...');
                            } else {
                                debugLog('🔑 Using Client Credentials token for Spotify');
                            }
                        } catch (e) {
                            debugWarn('Failed to get Client Credentials token:', e.message);
                        }
                    }
                
                    if (!token) {
                        throw new Error('No access token available');
                    }
        
                    const { params, ...restOptions } = options;
        
                    let finalUrl = `${this.baseUrl}${endpoint}`;
                    if (params) {
                        const searchParams = new URLSearchParams();
                        Object.entries(params).forEach(([key, value]) => {
                            if (value !== undefined && value !== null) {
                                searchParams.append(key, value);
                            }
                        });
                        const queryString = searchParams.toString();
                        if (queryString) {
                            finalUrl += `?${queryString}`;
                        }
                    }
        
                    const response = await fetch(finalUrl, {
                        ...restOptions,
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                            'Accept-Language': 'en-US',
                            ...options.headers
                        }
                    });
        
                    if (!response.ok) {
                        // Se è un errore di autenticazione (401), fallisci SUBITO senza retry
                        if (response.status === 401) {
                            throw new Error('Authentication required');
                        }
                        
                        // Gestione Rate Limiting (429)
                        if (response.status === 429) {
                            const retryAfter = parseInt(response.headers.get('Retry-After') || '5', 10);
                            const waitTime = retryAfter * 1000;
                            
                            debugWarn(`[Spotify] Rate limit hit! Waiting ${retryAfter}s before retry...`);
                            this.rateLimitResetTime = Date.now() + waitTime;
                            
                            // Notifica AuthService per switchare credenziale IMMEDIATAMENTE
                            if (!isUserToken) {
                                AuthService.handleRateLimitError(retryAfter);
                                localStorage.removeItem('spotify_client_token');
                            }
                            
                            // Se è l'ultimo tentativo, fallisci silenziosamente
                            if (attempt === maxRetries - 1) {
                                debugWarn(`[Spotify] Rate limit exceeded after ${maxRetries} attempts. All credentials may be limited.`);
                                return null;
                            }
                            
                            // Aspetta ma non troppo se ci sono altre credenziali disponibili
                            const minWait = Math.min(waitTime, 2000);
                            await this.delay(minWait);
                            
                            // Crea errore per forzare nuovo token alla prossima iterazione
                            const error = new Error('429 Rate limit');
                            error.status = 429;
                            throw error;
                        }
                        
                        const errorText = await response.text();
                        throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
                    }
        
                    const data = await response.json();
                    this.setCache(cacheKey, data);
                    this.pendingRequests.delete(cacheKey);
                    return data;
            
                } catch (error) {
                    lastError = error;
                    
                    // Se è un errore di autenticazione (401), fermati SUBITO
                    if (error.message === 'Authentication required' || (error.message && error.message.includes('401'))) {
                        debugWarn('[Spotify] Authentication required - stopping retries');
                        this.pendingRequests.delete(cacheKey);
                        throw error;
                    }
                    
                    // Se è rate limiting e non è l'ultimo tentativo, riprova con nuova credenziale
                    if (error.message && error.message.includes('429') && attempt < maxRetries - 1) {
                        continue;
                    }
                    
                    // Se non è rate limiting e non è l'ultimo tentativo, riprova
                    if ((!error.message || !error.message.includes('429')) && attempt < maxRetries - 1) {
                        continue;
                    }
                    
                    // Se è l'ultimo tentativo
                    if (attempt === maxRetries - 1) {
                        console.error(`[Spotify] Request failed after ${maxRetries} attempts:`, error.message);
                        this.pendingRequests.delete(cacheKey);
                        throw error;
                    }
                }
            }
            
            // Se arriviamo qui, tutti i tentativi sono falliti
            this.pendingRequests.delete(cacheKey);
            throw lastError || new Error('Request failed');
        })();
        
        // Aggiungi alla mappa pending DOPO che la promise è stata creata
        this.pendingRequests.set(cacheKey, requestPromise);
        
        return requestPromise;
    }

    async getUserProfile() {
        return this.request('/me');
    }

    async getTopArtists(timeRange = 'medium_term', limit = 20) {
        return this.request('/me/top/artists', {
            params: {
                time_range: timeRange,
                limit
            }
        });
    }

    async getArtistTopTracks(artistId) {
        return this.request(`/artists/${artistId}/top-tracks`, {
            params: { 
                market: 'US' // Usa mercato fisso invece di from_token
            }
        });
    }

    async getFollowedArtists() {
        const response = await this.request('/me/following', {
            params: {
                type: 'artist',
                limit: 50
            }
        });
        return response?.artists?.items || [];
    }

    async getArtistAlbums(artistId, limit = 20) {
        const result = await this.request(`/artists/${artistId}/albums`, {
            params: { 
                include_groups: 'album',
                limit,
                market: 'IT'
            }
        });
        
        if (!result) {
            return { items: [] };
        }
        
        return result;
    }

    async getArtistTopTracks(artistId, market = 'IT') {
        // Usa cache key specifica per top tracks (cache più lunga)
        const cacheKey = `top-tracks-${artistId}-${market}`;
        const cached = this.getFromCache(cacheKey);
        if (cached) {
            return cached;
        }
        
        const result = await this.request(`/artists/${artistId}/top-tracks`, {
            params: { 
                market
            }
        });
        
        if (!result) {
            return { tracks: [] };
        }
        
        // Cache top tracks per 30 minuti (più lungo di default 5 minuti)
        // Usa la stessa struttura della cache standard
        const cacheEntry = {
            data: result,
            timestamp: Date.now(),
            expiration: Date.now() + (30 * 60 * 1000) // 30 minuti
        };
        this.cache.set(cacheKey, cacheEntry);
        
        return result;
    }
    
    /**
     * Carica top tracks per più artisti in parallelo (batch)
     * Ottimizzazione per caricare più artisti contemporaneamente
     */
    async getMultipleArtistsTopTracks(artistIds, market = 'IT') {
        if (!artistIds || artistIds.length === 0) {
            return {};
        }
        
        // Filtra artisti già in cache
        const uncachedIds = [];
        const cachedResults = {};
        
        for (const artistId of artistIds) {
            const cacheKey = `top-tracks-${artistId}-${market}`;
            const cached = this.getFromCache(cacheKey);
            if (cached) {
                cachedResults[artistId] = cached;
            } else {
                uncachedIds.push(artistId);
            }
        }
        
        // Carica in parallelo solo quelli non in cache
        if (uncachedIds.length > 0) {
            const promises = uncachedIds.map(artistId => 
                this.getArtistTopTracks(artistId, market).catch(err => {
                    debugWarn(`Failed to load top tracks for ${artistId}:`, err.message);
                    return { tracks: [] };
                })
            );
            
            const results = await Promise.all(promises);
            
            // Combina risultati
            uncachedIds.forEach((artistId, index) => {
                cachedResults[artistId] = results[index];
            });
        }
        
        return cachedResults;
    }

    async getAlternativeRecommendations() {
        try {
            // 1. Get user's top artists
            const topArtists = await this.getTopArtists('medium_term', 20);

            if (!topArtists?.items?.length) {
                throw new Error('No top artists found');
            }

            // 2. Get currently followed artists
            const followedArtists = await this.getFollowedArtists();
            const followedIds = new Set(followedArtists.map(artist => artist.id));

            // 3. Calculate average popularity
            const avgPopularity = Math.floor(
                topArtists.items.reduce((sum, artist) => sum + artist.popularity, 0) / 
                topArtists.items.length
            );

            // 4. Get user genres
            const userGenres = new Set(
                topArtists.items.flatMap(artist => artist.genres)
            );

            // 5. Get artists with similar popularity and genres
            const recommendedArtists = topArtists.items
                .filter(artist => {
                    if (followedIds.has(artist.id)) return false;
                    
                    const popularityMatch = Math.abs(artist.popularity - avgPopularity) <= 20;
                    const genreMatch = artist.genres.some(genre => userGenres.has(genre));
                    
                    return popularityMatch && genreMatch;
                });

            // 6. Get albums for each recommended artist (con gestione rate limiting migliorata)
            const artistsWithAlbums = [];
            const selectedArtists = this.shuffleArray(recommendedArtists).slice(0, 10);
            
            for (const artist of selectedArtists) {
                try {
                    const albumsResponse = await this.getArtistAlbums(artist.id);
                    artistsWithAlbums.push({
                        ...artist,
                        albums: albumsResponse?.items || []
                    });
                    
                    // Aggiungi un piccolo delay tra le richieste
                    await this.delay(150);
                } catch (error) {
                    debugWarn(`[Spotify] Could not load albums for ${artist.name}`);
                    artistsWithAlbums.push({
                        ...artist,
                        albums: []
                    });
                }
            }

            return artistsWithAlbums;

        } catch (error) {
            console.error('[Spotify] getAlternativeRecommendations error:', error);
            throw error;
        }
    }

    getCacheKey(endpoint, options) {
        return `${endpoint}-${JSON.stringify(options)}`;
    }

    getFromCache(key) {
        const cached = this.cache.get(key);
        if (!cached) return null;

        const { timestamp, data, expiration } = cached;
        // Usa expiration personalizzata se presente, altrimenti usa cacheExpiration default
        const expirationTime = expiration || (timestamp + this.cacheExpiration);
        if (Date.now() > expirationTime) {
            this.cache.delete(key);
            return null;
        }

        return data;
    }

    setCache(key, data) {
        this.cache.set(key, {
            timestamp: Date.now(),
            data
        });
    }

    shuffleArray(array) {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }

    /**
     * Verifica se il nome matcha (uguale, quasi esatto, o contiene come parola)
     * Ritorna true se matcha, false altrimenti
     */
    isNameMatch(requestedName, resultName) {
        const normalizedRequested = this.normalizeArtistName(requestedName);
        const normalizedResult = this.normalizeArtistName(resultName);
        
        // Match esatto
        if (normalizedRequested === normalizedResult) {
            return true;
        }
        
        // Match se il nome richiesto è una parola completa nel risultato
        // Es: "Zucchero" in "Zucchero Fornaciari" → ACCETTA (nome corto in risultato lungo)
        const resultWords = normalizedResult.split(/\s+/);
        if (resultWords.includes(normalizedRequested)) {
            return true;
        }
        
        // Match se il risultato è una parola completa nel nome richiesto
        // MA solo se il nome richiesto ha UNA SOLA parola (altrimenti è troppo permissivo)
        // Es: "Zucchero" (1 parola) in "Zucchero Fornaciari" → NO, troppo permissivo
        // Es: "Ligabue" (1 parola) in "Luciano Ligabue" → NO, troppo permissivo
        // Solo se il nome richiesto ha 1 parola: "Anna" in "Anna Castiglia" → NO, non accettare
        const requestedWords = normalizedRequested.split(/\s+/);
        // Se il nome richiesto ha più di 1 parola, NON accettare match parziali (troppo permissivo)
        if (requestedWords.length > 1) {
            // Nome richiesto ha più parole → il risultato deve contenere TUTTE le parole principali
            // Es: "Anna Castiglia" → risultato deve contenere sia "Anna" che "Castiglia"
            const mainWords = requestedWords.filter(w => w.length > 2); // Filtra parole corte (a, il, etc)
            const allWordsMatch = mainWords.every(word => normalizedResult.includes(word));
            if (allWordsMatch) {
                return true;
            }
            // Se non tutte le parole matchano, non accettare
            return false;
        }
        // Se il nome richiesto ha 1 sola parola, accetta se il risultato la contiene
        if (requestedWords.length === 1 && normalizedResult.includes(normalizedRequested)) {
            return true;
        }
        
        // Match quasi esatto: Levenshtein distance molto bassa (max 15% di differenza)
        const distance = this.levenshteinDistance(normalizedRequested, normalizedResult);
        const maxLen = Math.max(normalizedRequested.length, normalizedResult.length);
        const similarity = 1 - (distance / maxLen);
        
        // Considera match se similarity >= 0.85 (max 15% di differenza)
        return similarity >= 0.85;
    }

    /**
     * Converte originHint (paese) in market code Spotify
     */
    getMarketFromOrigin(originHint) {
        if (!originHint) return 'US'; // Default
        
        const originLower = originHint.toLowerCase().trim();
        const marketMap = {
            'italy': 'IT',
            'italia': 'IT',
            'france': 'FR',
            'spain': 'ES',
            'spagna': 'ES',
            'germany': 'DE',
            'germania': 'DE',
            'uk': 'GB',
            'united kingdom': 'GB',
            'usa': 'US',
            'united states': 'US',
            'philippines': 'PH',
            'japan': 'JP',
            'south korea': 'KR',
            'china': 'CN',
            'brazil': 'BR',
            'brasile': 'BR',
            'mexico': 'MX',
            'canada': 'CA',
            'australia': 'AU',
            'sweden': 'SE',
            'norway': 'NO',
            'denmark': 'DK',
            'netherlands': 'NL',
            'belgium': 'BE',
            'portugal': 'PT',
            'greece': 'GR',
            'poland': 'PL',
            'russia': 'RU',
            'turkey': 'TR',
            'argentina': 'AR',
            'chile': 'CL',
            'colombia': 'CO',
            'peru': 'PE',
            'india': 'IN'
        };
        
        return marketMap[originLower] || 'US'; // Default a US se non trovato
    }

    /**
     * Verifica match generi tra hint da Gemini e generi Spotify
     * Ritorna score 0-1
     */
    calculateGenreMatchScore(genreHint, spotifyGenres) {
        if (!genreHint || !spotifyGenres || spotifyGenres.length === 0) {
            return 0.5; // Se non abbiamo hint, non penalizziamo
        }
        
        const hintWords = genreHint.toLowerCase().split(/\s+/).filter(w => w.length > 2);
        const spotifyGenresStr = spotifyGenres.join(' ').toLowerCase();
        
        if (hintWords.length === 0) {
            return 0.5;
        }
        
        // Conta quante parole del genere hint sono presenti nei generi Spotify
        let matches = 0;
        hintWords.forEach(word => {
            if (spotifyGenresStr.includes(word)) {
                matches++;
            }
        });
        
        // Score basato su percentuale di match
        return matches / hintWords.length;
    }

    async searchArtist(artistName, spotifyId = null, genreHint = null, originHint = null, skipAlbums = false) {
        try {
            // Se abbiamo lo Spotify ID, recupera direttamente l'artista (evita omonimi!)
            if (spotifyId) {
                debugLog(`[Spotify] Fetching artist directly by ID: ${spotifyId}`);
                const artist = await this.request(`/artists/${spotifyId}`);
                
                if (artist) {
                    // Ottieni anche gli album dell'artista (con gestione rate limiting) SOLO se richiesto
                    if (!skipAlbums) {
                        const albumsResponse = await this.getArtistAlbums(artist.id);
                        return {
                            ...artist,
                            albums: albumsResponse?.items || []
                        };
                    } else {
                        // Versione leggera: solo artista, senza album
                        return {
                            ...artist,
                            albums: []
                        };
                    }
                }
            }
            
            // LOGICA SEMPLIFICATA: query solo nome + nazione (senza generi)
            debugLog(`[Spotify] Searching artist by name: "${artistName}"${originHint ? ` (origin: ${originHint})` : ''}`);
            
            // Converti origin in market code
            const market = this.getMarketFromOrigin(originHint);
            debugLog(`[Spotify] Using market: ${market} (from origin: ${originHint || 'none'})`);
            
            // Query: solo nome (senza generi, senza nazione)
            const query = artistName;
            
            debugLog(`[Spotify] 🔍 Final query: "${query}" (market: ${market})`);
            
            // Cerca con query solo nome
            let response = await this.request('/search', {
                params: {
                    q: query,
                    type: 'artist',
                    market: market,
                    limit: 5
                }
            });
            
            // Se non trova risultati, prova variante normalizzata (senza accenti)
            if ((!response || !response.artists?.items?.length || response.artists.items.length === 0) && 
                artistName !== artistName.normalize('NFD').replace(/[\u0300-\u036f]/g, '')) {
                const normalizedName = artistName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                debugLog(`[Spotify] Trying normalized variant: "${normalizedName}"`);
                const normalizedResponse = await this.request('/search', {
                    params: {
                        q: normalizedName,
                        type: 'artist',
                        market: market,
                        limit: 5
                    }
                });
                if (normalizedResponse && normalizedResponse.artists?.items?.length > 0) {
                    response = normalizedResponse;
                }
            }

            // Gestisci rate limiting o nessun risultato
            if (!response || !response.artists?.items?.length) {
                debugWarn(`[Spotify] ❌ No results found for "${artistName}" - returning null (not inventing)`);
                return null;
            }

            // LOG: mostra tutti i risultati di Spotify per debug
            debugLog(`[Spotify] Search results for "${artistName}":`, 
                response.artists.items.map(a => ({
                    name: a.name,
                    popularity: a.popularity,
                    genres: a.genres
                }))
            );
            
            // STEP 1: Filtra soundtrack/colonne sonore
            const validArtists = response.artists.items.filter(a => {
                const name = a.name.toLowerCase();
                const genres = a.genres.join(' ').toLowerCase();
                
                const isSoundtrack = genres.includes('soundtrack') || 
                                   genres.includes('disney') ||
                                   name.includes('cast') ||
                                   name.includes('soundtrack');
                
                return !isSoundtrack;
            });

            if (validArtists.length === 0) {
                debugWarn(`[Spotify] ❌ No valid artists after filtering for "${artistName}" - returning null`);
                return null;
            }

            // STEP 2: Priorità al match ESATTO verificato, poi agli altri
            let selectedArtist = null;
            let exactMatchesVerified = [];
            let exactMatchesNotVerified = [];
            let partialMatchesVerified = [];
            let partialMatchesNotVerified = [];
            
            const normalizedRequested = this.normalizeArtistName(artistName);
            
            for (let i = 0; i < validArtists.length; i++) {
                const candidate = validArtists[i];
                
                // Verifica: se non ha followers o non ha foto → passa al prossimo
                const hasFollowers = (candidate.followers?.total || 0) > 0;
                const hasImage = candidate.images && candidate.images.length > 0;
                
                if (!hasFollowers || !hasImage) {
                    debugWarn(`[Spotify] ⏭️ Skipping "${candidate.name}" - no followers or no image`);
                    continue;
                }
                
                // Verifica se l'artista è verificato
                const isVerified = candidate.verified === true;
                
                // Verifica match del nome
                const normalizedCandidate = this.normalizeArtistName(candidate.name);
                const isExactMatch = normalizedRequested === normalizedCandidate;
                const nameMatches = this.isNameMatch(artistName, candidate.name);
                
                if (isExactMatch) {
                    // Match ESATTO → salva in base a verified
                    if (isVerified) {
                        exactMatchesVerified.push(candidate);
                        debugLog(`[Spotify] ✅ Found EXACT VERIFIED match: "${candidate.name}" (requested: "${artistName}")`);
                    } else {
                        exactMatchesNotVerified.push(candidate);
                        debugLog(`[Spotify] ✅ Found EXACT match (not verified): "${candidate.name}" (requested: "${artistName}")`);
                    }
                } else if (nameMatches) {
                    // Match parziale → salva in base a verified
                    if (isVerified) {
                        partialMatchesVerified.push(candidate);
                        debugLog(`[Spotify] ✅ Found PARTIAL VERIFIED match: "${candidate.name}" (requested: "${artistName}")`);
                    } else {
                        partialMatchesNotVerified.push(candidate);
                        debugLog(`[Spotify] ✅ Found PARTIAL match (not verified): "${candidate.name}" (requested: "${artistName}")`);
                    }
                } else {
                    debugWarn(`[Spotify] ⏭️ Skipping "${candidate.name}" - name doesn't match "${artistName}"`);
                }
            }
            
            // PRIORITÀ: 1) Match esatto verificato, 2) Match parziale verificato, 3) Match esatto non verificato, 4) Match parziale non verificato
            if (exactMatchesVerified.length > 0) {
                // Ordina per followers e prendi il più popolare tra i verificati
                exactMatchesVerified.sort((a, b) => (b.followers?.total || 0) - (a.followers?.total || 0));
                selectedArtist = exactMatchesVerified[0];
                debugLog(`[Spotify] ✅ Selected EXACT VERIFIED match "${selectedArtist.name}" (requested: "${artistName}") - ${selectedArtist.followers?.total || 0} followers`);
            } else if (partialMatchesVerified.length > 0) {
                // Se non c'è match esatto verificato, preferisci match parziale verificato
                partialMatchesVerified.sort((a, b) => (b.followers?.total || 0) - (a.followers?.total || 0));
                selectedArtist = partialMatchesVerified[0];
                debugLog(`[Spotify] ✅ Selected PARTIAL VERIFIED match "${selectedArtist.name}" (requested: "${artistName}") - ${selectedArtist.followers?.total || 0} followers (verified preferred over exact non-verified)`);
            } else if (exactMatchesNotVerified.length > 0) {
                // Altrimenti match esatto non verificato
                exactMatchesNotVerified.sort((a, b) => (b.followers?.total || 0) - (a.followers?.total || 0));
                selectedArtist = exactMatchesNotVerified[0];
                debugLog(`[Spotify] ✅ Selected EXACT match (not verified) "${selectedArtist.name}" (requested: "${artistName}") - ${selectedArtist.followers?.total || 0} followers`);
            } else if (partialMatchesNotVerified.length > 0) {
                // Ultima opzione: match parziale non verificato
                partialMatchesNotVerified.sort((a, b) => (b.followers?.total || 0) - (a.followers?.total || 0));
                selectedArtist = partialMatchesNotVerified[0];
                debugLog(`[Spotify] ✅ Selected PARTIAL match (not verified) "${selectedArtist.name}" (requested: "${artistName}") - ${selectedArtist.followers?.total || 0} followers`);
            }
            
            // Se nessuno soddisfa i criteri, non mostrare nulla
            if (!selectedArtist) {
                debugWarn(`[Spotify] ❌ No artist with followers and image for "${artistName}" - returning null`);
                return null;
            }
            
            // Ottieni anche gli album dell'artista (con gestione rate limiting) SOLO se richiesto
            if (!skipAlbums) {
                const albumsResponse = await this.getArtistAlbums(selectedArtist.id);
                return {
                    ...selectedArtist,
                    albums: albumsResponse?.items || []
                };
            } else {
                // Versione leggera: solo artista, senza album
                return {
                    ...selectedArtist,
                    albums: []
                };
            }

        } catch (error) {
            console.error(`[Spotify] searchArtist error for "${artistName}":`, error.message);
            return null;
        }
    }

    // Calculate similarity score between query and artist name
    calculateNameSimilarity(artistName, query) {
        const name = artistName.toLowerCase();
        const q = query.toLowerCase();
        
        // Exact match
        if (name === q) return 1000;
        
        // Starts with query
        if (name.startsWith(q)) return 500;
        
        // Contains query
        if (name.includes(q)) return 250;
        
        // Word in name starts with query
        const words = name.split(/\s+/);
        for (const word of words) {
            if (word.startsWith(q)) return 400;
        }
        
        // Levenshtein distance (lower is better)
        const distance = this.levenshteinDistance(name, q);
        const maxLen = Math.max(name.length, q.length);
        const similarity = (maxLen - distance) / maxLen;
        
        return similarity * 100;
    }

    // Levenshtein distance algorithm
    levenshteinDistance(s1, s2) {
        const m = s1.length;
        const n = s2.length;
        const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

        for (let i = 0; i <= m; i++) dp[i][0] = i;
        for (let j = 0; j <= n; j++) dp[0][j] = j;

        for (let i = 1; i <= m; i++) {
            for (let j = 1; j <= n; j++) {
                if (s1[i-1] === s2[j-1]) {
                    dp[i][j] = dp[i-1][j-1];
                } else {
                    dp[i][j] = Math.min(
                        dp[i-1][j] + 1,
                        dp[i][j-1] + 1,
                        dp[i-1][j-1] + 1
                    );
                }
            }
        }

        return dp[m][n];
    }

    async searchArtistSuggestions(query, limit = 6) {
        try {
            const response = await this.request('/search', {
                params: {
                    q: query,
                    type: 'artist',
                    limit: 20 // Fetch more to filter and sort
                }
            });

            if (!response || !response.artists?.items?.length) {
                return [];
            }

            // Filter out soundtracks and invalid artists
            const validArtists = response.artists.items.filter(artist => {
                const name = artist.name.toLowerCase();
                const genres = artist.genres.join(' ').toLowerCase();
                
                // Exclude soundtracks, compilations, etc.
                const isInvalid = genres.includes('soundtrack') || 
                                 genres.includes('disney') ||
                                 name.includes('cast') ||
                                 name.includes('soundtrack') ||
                                 name.includes('various artists');
                
                return !isInvalid;
            });

            // Calculate combined score: name similarity (70%) + popularity (30%)
            const scoredArtists = validArtists.map(artist => {
                const nameScore = this.calculateNameSimilarity(artist.name, query);
                const popularityScore = artist.popularity || 0;
                
                // Name match is MORE important than popularity
                const finalScore = (nameScore * 0.7) + (popularityScore * 0.3);
                
                return {
                    ...artist,
                    _score: finalScore
                };
            });

            // Sort by combined score (highest first)
            const sorted = scoredArtists
                .sort((a, b) => b._score - a._score)
                .slice(0, limit);

            // Return formatted data for autocomplete
            return sorted.map(artist => ({
                name: artist.name,
                spotifyId: artist.id,
                imageUrl: artist.images[0]?.url || null,
                popularity: artist.popularity,
                followers: artist.followers?.total || 0,
                genres: artist.genres
            }));

        } catch (error) {
            console.error('Spotify search suggestions failed:', error);
            return [];
        }
    }

    async searchTrackSuggestions(query, limit = 6) {
        try {
            const response = await this.request('/search', {
                params: {
                    q: query,
                    type: 'track',
                    limit: 20 // Fetch more to filter and sort
                }
            });

            if (!response || !response.tracks?.items?.length) {
                return [];
            }

            // Filter out invalid tracks (remixes, instrumentals, karaoke, etc.)
            const validTracks = response.tracks.items.filter(track => {
                const name = track.name.toLowerCase();
                const isInvalid = name.includes('(karaoke)') || 
                                 name.includes('(instrumental)') ||
                                 name.includes('(acapella)') ||
                                 name.includes('(remix)') ||
                                 track.is_local;
                
                return !isInvalid;
            });

            // Calculate combined score: name similarity (70%) + popularity (30%)
            const scoredTracks = validTracks.map(track => {
                const nameScore = this.calculateNameSimilarity(track.name, query);
                const popularityScore = track.popularity || 0;
                
                // Name match is MORE important than popularity
                const finalScore = (nameScore * 0.7) + (popularityScore * 0.3);
                
                return {
                    ...track,
                    _score: finalScore
                };
            });

            // Sort by combined score (highest first)
            const sorted = scoredTracks
                .sort((a, b) => b._score - a._score)
                .slice(0, limit);

            // Return formatted data for autocomplete
            return sorted.map(track => ({
                name: track.name,
                artistName: track.artists[0]?.name || 'Unknown Artist',
                spotifyId: track.id,
                spotifyUri: track.uri,
                imageUrl: track.album?.images?.[0]?.url || null,
                albumImageUrl: track.album?.images?.[0]?.url || null,
                popularity: track.popularity,
                external_urls: track.external_urls,
                album: track.album,
                artists: track.artists
            }));

        } catch (error) {
            console.error('Spotify track search suggestions failed:', error);
            return [];
        }
    }

    async getRandomPopularArtists(limit = 6) {
        try {
            // Generi popolari per trovare artisti diversi
            const popularGenres = [
                'pop', 'rock', 'hip-hop', 'indie', 'electronic',
                'r-n-b', 'alternative', 'dance', 'country', 'jazz'
            ];

            // Prendi un genere random
            const randomGenre = popularGenres[Math.floor(Math.random() * popularGenres.length)];
            
            // Cerca artisti popolari per quel genere
            const response = await this.request('/search', {
                params: {
                    q: `genre:${randomGenre}`,
                    type: 'artist',
                    limit: 50 // Fetch molti per avere varietà
                }
            });

            if (!response || !response.artists?.items?.length) {
                return [];
            }

            // Filtra artisti validi con immagini
            const validArtists = response.artists.items.filter(artist => {
                const hasImage = artist.images && artist.images.length > 0;
                const isPopular = artist.popularity > 50; // Solo artisti abbastanza popolari
                return hasImage && isPopular;
            });

            // Shuffle e prendi limit random
            const shuffled = this.shuffleArray(validArtists);
            const selected = shuffled.slice(0, limit);

            return selected.map(artist => ({
                name: artist.name,
                spotifyId: artist.id,
                imageUrl: artist.images[0]?.url || null,
                popularity: artist.popularity,
                // Dimensione random tra 60px (min) e 95px (max)
                size: 60 + Math.floor(Math.random() * 36) // 60-95px
            }));

        } catch (error) {
            console.error('Failed to get random artists:', error);
            return [];
        }
    }
}

const spotifyService = new SpotifyService();

export default spotifyService;

// Export funzioni specifiche (skipAlbums: true in filter per non caricare album, risparmio chiamate)
export const searchSpotifyArtist = (artistName, spotifyId = null, genreHint = null, originHint = null, skipAlbums = false) =>
    spotifyService.searchArtist(artistName, spotifyId, genreHint, originHint, skipAlbums);
export const searchArtistSuggestions = (query, limit) => spotifyService.searchArtistSuggestions(query, limit);
